import { z } from 'zod';
import { getMaxBase10Number, mapBase10ToBase36 } from './numberBase36Utils';
const badgeNumberTotalLength = 25;
const badgeIssuerIdBase36Digits = 3;
const cardIdBase36Digits = 16;
const issuanceNoBase36Digits = 2;
const maxBadgeIssuerId = getMaxBase10Number(badgeIssuerIdBase36Digits);
const maxCardId = getMaxBase10Number(cardIdBase36Digits);
const maxIssuanceNo = getMaxBase10Number(issuanceNoBase36Digits);
export const aamvaBadgeNumberInputDataSchema = z.object({
    /*
        %2s – 2 characters
        in rms classic:
            e.g. ME, TX
            comes from `config::get('STATE_ID')` - app deployment config?
    */
    stateId: z.string().length(2),
    /*
        %03s – 3 characters, padded with zeroes if needed
        in rms classic:
            e.g. 001 (always)
            comes from `SELECT app_id FROM app_mode WHERE app_mode = $1`
        badging:
            using base-36, 3 chars = 0-46655
    */
    badgeIssuerId: z.number().int().min(0).max(maxBadgeIssuerId),
    /*
        %1s – 1 character
        in rms classic:
            e.g. A (always) - constant
    */
    badgeVersion: z.string().length(1),
    /*
        %07s – 7 characters, padded with zeroes
        in rms classic:
            e.g. 0000001, 0000009, 000000A, 000000L
            comes from SQL ID "unique_badge_id_seq" -> LAD from_decimal() get_lad() - matches "badge_id" column
        badging:
            using base-36, 7 chars = 0-78364164095
    */
    cardId: z.number().int().min(0).max(maxCardId),
    /*
        %02s – 2 characters, padded with zeroes
        in rms classic:
            00 (only seen this), technically should be 00, 01, 02... depending on which re-issue
        badging:
            using base-36
    */
    issuanceNo: z.number().int().min(0).max(maxIssuanceNo),
});
export const aamvaBadgeNumberOutputDataSchema = z.object({
    /*
        %1s – 1 character
        in rms classic:
            e.g. G, D, 8, 9, 4, L
            comes from gen_check_digit(): hash-md5(%2s%03s%1s%07s%02s - state, issuer, card, issue) -> to binary -> take first byte -> convert to character
    */
    badgeSingleCheckDigit: z.string().length(1),
});
/**
 * Generate Badge Number in correct format
 *
 * in Spec:
 * * Maximum recommended length is 25 according to spec (DAQ element, V25ANS)
 *
 * in badging lib:
 *  * format - %2s%03s%1s%1s%16s%02s (25 characters)
 *
 * in rms classic:
 *  * format - %2s%03s%1s%1s%07s%02s (16 characters)
 *  * e.g. ME001GA000000300, TX0014A000000100
 *
 *
 * @param context
 * @param badgeNumberData - data from which to generate Badge Number
 */
export function generateAamvaPartDaqBadgeNumber(context, badgeNumberData) {
    const { stateId, badgeIssuerId, badgeVersion, cardId, issuanceNo } = aamvaBadgeNumberInputDataSchema.parse(badgeNumberData);
    // base36 - AAMVA spec only allows base10 numbers in fields, but these are internal - part for DAQ BadgeNumber which is not a number
    const badgeIssuerIdStr = badgeIssuerId.toString(36).padStart(badgeIssuerIdBase36Digits, '0');
    const cardIdStr = cardId.toString(36).padStart(cardIdBase36Digits, '0');
    const issuanceNoStr = issuanceNo.toString(36).padStart(issuanceNoBase36Digits, '0');
    const hashedString = context.encryption.hash('MD5', [badgeNumberData.stateId, badgeIssuerIdStr, badgeVersion, cardIdStr, issuanceNoStr].join());
    const hashFirstByte = parseInt(hashedString.slice(0, 2), 16);
    const badgeSingleCheckDigitStr = mapBase10ToBase36(hashFirstByte);
    const badgeNumber = `${stateId}${badgeIssuerIdStr}${badgeSingleCheckDigitStr}${badgeVersion}${cardIdStr}${issuanceNoStr}`;
    if (badgeNumber.length !== badgeNumberTotalLength) {
        throw new Error(`Generated badge number is incorrect length: expected${badgeNumberTotalLength}, got ${badgeNumber.length}`);
    }
    return badgeNumber;
}
